import {
  LooplayIframeAuth,
  type EmitGameEventResponse,
  type GameEventKey,
} from '@looplay/sdk'
const looplayAuth = new LooplayIframeAuth({
  parentOrigin: import.meta.env.VITE_PARENT_ORIGIN,
  appId: import.meta.env.VITE_LOOPLAY_APP_ID,
  apiUrl: import.meta.env.VITE_LOOPLAY_API_URL,
  onDebug: (message, details) => {
    if (details) {
      console.debug(`[Looplay] ${message}`, details)
      return
    }
    console.debug(`[Looplay] ${message}`)
  },
})
export const getLooplayJwt = () => looplayAuth.getJwt()
export const getLooplayApiUrl = () => looplayAuth.getApiUrl()
export const getLooplayGameId = () => looplayAuth.getGameId()
export const canTrackLooplay = () => looplayAuth.canTrack()
export const subscribeLooplayAuth = looplayAuth.subscribe.bind(looplayAuth)
export const requestLooplayAuth = () => looplayAuth.requestAuth()
/** Call once when the game view opens, before any play/match tracking. */
export const trackLooplayView = () => looplayAuth.trackView()
/** Call when the user presses Start Game to begin a new attempt. */
export const startLooplayAttempt = () => looplayAuth.startAttempt()
export const initializeLooplayAuth = () => looplayAuth.init()
export const getLooplayClient = () => looplayAuth.getClient()
const normalizePlayTimeSeconds = (playTimeSeconds?: number) => {
  if (playTimeSeconds === undefined) return undefined
  if (!Number.isFinite(playTimeSeconds)) return undefined
  return Math.max(0, Math.round(playTimeSeconds))
}
export type LooplayPlayTrackingType = 'round' | 'survival'
export interface ResolveTrackPlayTimeOptions {
  trackingType: LooplayPlayTrackingType
  roundSeconds?: number
  elapsedSeconds?: number
  startedAtMs?: number
  nowMs?: number
}
export const resolveTrackPlayTimeSeconds = ({
  trackingType,
  roundSeconds,
  elapsedSeconds,
  startedAtMs,
  nowMs = Date.now(),
}: ResolveTrackPlayTimeOptions) => {
  if (trackingType === 'round') {
    return normalizePlayTimeSeconds(roundSeconds ?? elapsedSeconds)
  }
  if (elapsedSeconds !== undefined) {
    return normalizePlayTimeSeconds(elapsedSeconds)
  }
  if (startedAtMs === undefined || !Number.isFinite(startedAtMs)) {
    return undefined
  }
  return normalizePlayTimeSeconds((nowMs - startedAtMs) / 1000)
}
export const trackPlay = (playTimeSeconds?: number) =>
  looplayAuth.trackPlay(normalizePlayTimeSeconds(playTimeSeconds))
export const trackPlayByType = (options: ResolveTrackPlayTimeOptions) =>
  trackPlay(resolveTrackPlayTimeSeconds(options))
export const trackMatch = (
  matchId: string,
  options: { durationSeconds: number; isCompleted?: boolean; isWin?: boolean },
) => looplayAuth.trackMatch(matchId, options)
export const emitGameEvent = (
  actionCode: GameEventKey | string,
  options?: { value?: number; refId?: string; payload?: Record<string, unknown> },
): Promise<EmitGameEventResponse | null> => looplayAuth.emitGameEvent(actionCode, options)
export const initializeLooplayLifecycleTracking = () => looplayAuth.initLifecycleTracking()